import React from 'react';
import PropTypes from 'prop-types';
import Video from './Video';
import AbstractVideoTrack from './AbstractVideoTrack';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import { connect } from 'store/modules/base/redux';

/**
 * Component that renders a video element for a passed in video track and
 * notifies the store when the video has started playing.
 *
 * @augments AbstractVideoTrack
 */
class VideoTrack extends AbstractVideoTrack {
  /**
   * Default values for {@code VideoTrack} component's properties.
   *
   * @static
   */
  static defaultProps = {
    className: '',

    id: ''
  };

  /**
   * Renders the video element.
   *
   * @override
   * @returns {ReactElement}
   */
  render() {
    const {
      _noAutoPlayVideo,
      className,
      id,
      muted,
      videoTrack,
      style,
      eventHandlers
    } = this.props;

    return (
      <Video
        autoPlay={!_noAutoPlayVideo}
        className={className}
        eventHandlers={eventHandlers}
        id={id}
        muted={muted}
        onVideoPlaying={this._onVideoPlaying}
        style={style}
        videoTrack={videoTrack}
      />
    );
  }
}

/**
 * Maps (parts of) the Redux state to the associated VideoTracks props.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     _noAutoPlayVideo: boolean
 * }}
 */
function _mapStateToProps(state) {
  const testingConfig = state['features/base/config'].testing;

  return {
    _noAutoPlayVideo: testingConfig?.noAutoPlayVideo
  };
}

export default React.memo(connect(_mapStateToProps)(withBoundary(VideoTrack)));
/**
 * The type of the React {@code Component} props of {@link Video}.
 */
VideoTrack.propTypes = {
  /**
   * CSS classes to add to the video element.
   */
  className: PropTypes.string,

  /**
   * The value of the id attribute of the video. Used by the torture tests to
   * locate video elements.
   */
  id: PropTypes.string,

  /**
   * Optional callback to invoke once the video starts playing.
   */
  onVideoPlaying: PropTypes.func,

  /**
   * The JitsiLocalTrack to display.
   */
  videoTrack: PropTypes.PropTypes.object,

  /**
   *
   * Used to determine the value of the autoplay attribute of the underlying
   * video element.
   */
  _noAutoPlayVideo: PropTypes.bool,

  /**
   * A map of the event handlers for the video HTML element.
   */
  eventHandlers: PropTypes.any,

  /**
   * A styles that will be applied on the video element.
   */
  style: PropTypes.object,

  /**
   * The value of the muted attribute for the underlying video element.
   */
  muted: PropTypes.bool
};
